import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUsersApi,
  getOrganizationsApi,
  updateUserApi,
  deleteUserApi,
  updateOrganizationApi,
  deleteOrganizationApi
} from '../services/apiService';
import { getUser, setUser, logout } from '../services/authService';
import './SuperAdminUsersOrgsPage.css';
import './OrganizationCreatePage.css';

function formatDate(d) {
  if (!d) return '—';
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? '—' : date.toLocaleString();
  } catch {
    return '—';
  }
}

function orgHubRowsFromOrg(org) {
  if (Array.isArray(org?.hubs) && org.hubs.length > 0) {
    const sorted = org.hubs.slice().sort((a, b) => (b.isPrimary === true) - (a.isPrimary === true));
    return {
      hubIds: sorted.map((h) => String(h.hubId)),
      primaryHubIndex: Math.max(0, sorted.findIndex((h) => h.isPrimary))
    };
  }
  if (org?.hubId != null) {
    return { hubIds: [String(org.hubId)], primaryHubIndex: 0 };
  }
  return { hubIds: [''], primaryHubIndex: 0 };
}

function buildOrgPayload(form) {
  const parsedHubIds = form.hubIds
    .map((s) => String(s ?? '').trim())
    .filter((s) => s.length > 0)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n > 0);
  const uniqueHubIds = Array.from(new Set(parsedHubIds));
  const primaryCandidate = String(form.hubIds[form.primaryHubIndex] ?? '').trim();
  const primaryHubId = primaryCandidate ? Number(primaryCandidate) : uniqueHubIds[0];

  return {
    name: form.name.trim(),
    organisationId: form.organisationId.trim() ? Number(form.organisationId) : null,
    hubId: primaryHubId,
    hubIds: uniqueHubIds,
    description: form.description.trim() || undefined,
    contactEmail: form.contactEmail.trim() || undefined,
    contactPhone: form.contactPhone.trim() || undefined,
    address: form.address.trim() || undefined,
    isActive: form.isActive
  };
}

export default function SuperAdminUsersOrgsPage() {
  const navigate = useNavigate();
  const currentUser = getUser();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const [userEdit, setUserEdit] = useState(null);
  const [orgEdit, setOrgEdit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [usersData, orgsData] = await Promise.all([getUsersApi(), getOrganizationsApi()]);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setOrganizations(Array.isArray(orgsData) ? orgsData : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const orgByName = organizations.reduce((acc, o) => {
    acc[o.name] = o;
    return acc;
  }, {});

  const openUserEdit = (u) => {
    setSuccess('');
    setUserEdit({
      id: u.id,
      name: u.name || '',
      email: u.email || '',
      organisationName: u.organisationName || '',
      role: u.role || 'User',
      isSuperAdmin: !!u.isSuperAdmin,
      password: ''
    });
  };

  const openOrgEdit = (o) => {
    setSuccess('');
    const { hubIds, primaryHubIndex } = orgHubRowsFromOrg(o);
    setOrgEdit({
      id: o.id,
      name: o.name || '',
      organisationId: o.organisationId != null ? String(o.organisationId) : '',
      hubIds,
      primaryHubIndex,
      description: o.description || '',
      contactEmail: o.contactEmail || '',
      contactPhone: o.contactPhone || '',
      address: o.address || '',
      isActive: o.isActive !== false
    });
  };

  const saveUserEdit = async (e) => {
    e.preventDefault();
    if (!userEdit) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const email = userEdit.email.trim().toLowerCase();
      const updated = await updateUserApi(userEdit.id, {
        name: userEdit.name.trim(),
        email,
        organisationName: userEdit.organisationName.trim(),
        role: userEdit.role.trim(),
        isSuperAdmin: userEdit.isSuperAdmin,
        password: userEdit.password.trim() || undefined
      });
      // Requests are identified by the stored email, so keep the session in step when editing yourself.
      if (currentUser && updated && updated.id === currentUser.id) {
        if (localStorage.getItem('solarRememberEmail')) {
          localStorage.setItem('solarRememberEmail', updated.email);
        }
        setUser({
          ...currentUser,
          name: updated.name,
          organisationName: updated.organisationName || '',
          role: updated.role || 'User',
          isSuperAdmin: !!updated.isSuperAdmin,
          email: updated.email
        });
      }
      setUserEdit(null);
      // An older backend ignores the email field and still returns success.
      const emailSaved = (updated?.email || '').toLowerCase() === email;
      await load();
      if (emailSaved) {
        setSuccess('User updated successfully.');
      } else {
        setError('Email was not changed: the server is running an older version that does not support email editing.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update user.');
    } finally {
      setBusy(false);
    }
  };

  const saveOrgEdit = async (e) => {
    e.preventDefault();
    if (!orgEdit) return;
    const payload = buildOrgPayload(orgEdit);
    if (!payload.name) {
      setError('Organisation name is required.');
      return;
    }
    if (!payload.hubIds?.length) {
      setError('At least one Hub ID is required.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await updateOrganizationApi(orgEdit.id, payload);
      setOrgEdit(null);
      setSuccess('Organisation updated successfully.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update organisation.');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      if (deleteTarget.type === 'user') {
        await deleteUserApi(deleteTarget.id);
        setSuccess('User deleted successfully.');
      } else {
        await deleteOrganizationApi(deleteTarget.id);
        setSuccess('Organisation deactivated successfully.');
      }
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="superadmin-page">
        <div className="superadmin-card">
          <p className="superadmin-loading">Loading users and organisations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="superadmin-page">
      <div className="superadmin-card">
        <div className="superadmin-header">
          <h1 className="superadmin-title">Users & Organisations</h1>
          <div className="superadmin-actions">
            <button type="button" className="org-btn-secondary" onClick={() => navigate('/admin/organizations/create')}>
              Create Organisation
            </button>
            <button type="button" className="org-btn-secondary" onClick={() => navigate('/settings')}>Settings</button>
            <button type="button" className="org-btn-logout" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        {error && <div className="org-alert org-alert-error">{error}</div>}
        {success && <div className="org-alert org-alert-success">{success}</div>}

        <section className="superadmin-section">
          <h2 className="superadmin-section-title">All Users</h2>
          <div className="superadmin-table-wrap">
            <table className="superadmin-table">
              <thead>
                <tr>
                  <th>User Id</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Organisation Name</th>
                  <th>Organisation Id</th>
                  <th>Org Description</th>
                  <th>Org Contact</th>
                  <th>Org Address</th>
                  <th>Super Admin</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="superadmin-empty">No users found.</td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const org = orgByName[u.organisationName];
                    const isSelf = currentUser && u.id === currentUser.id;
                    return (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{u.name || '—'}</td>
                        <td>{u.email || '—'}</td>
                        <td>{u.role || '—'}</td>
                        <td>{u.organisationName || '—'}</td>
                        <td>{org != null && org.organisationId != null ? org.organisationId : '—'}</td>
                        <td>{org?.description || '—'}</td>
                        <td>
                          {org?.contactEmail || org?.contactPhone
                            ? [org.contactEmail, org.contactPhone].filter(Boolean).join(' · ')
                            : '—'}
                        </td>
                        <td>{org?.address || '—'}</td>
                        <td>{u.isSuperAdmin ? 'Yes' : 'No'}</td>
                        <td>{formatDate(u.createdAt)}</td>
                        <td className="superadmin-actions-cell">
                          <button type="button" className="sa-btn-edit" onClick={() => openUserEdit(u)} disabled={busy}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="sa-btn-delete"
                            onClick={() => setDeleteTarget({ type: 'user', id: u.id, label: u.name || u.email })}
                            disabled={busy || isSelf}
                            title={isSelf ? 'Cannot delete your own account' : 'Delete user'}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="superadmin-section">
          <h2 className="superadmin-section-title">All Organisations</h2>
          <div className="superadmin-table-wrap">
            <table className="superadmin-table">
              <thead>
                <tr>
                  <th>Db Id</th>
                  <th>Organisation Id</th>
                  <th>Name</th>
                  <th>Hub ID</th>
                  <th>All Hub IDs</th>
                  <th>Description</th>
                  <th>Contact Email</th>
                  <th>Contact Phone</th>
                  <th>Address</th>
                  <th>Active</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="superadmin-empty">No organisations found.</td>
                  </tr>
                ) : (
                  organizations.map((o) => (
                    <tr key={o.id}>
                      <td>{o.id}</td>
                      <td>{o.organisationId != null ? o.organisationId : '—'}</td>
                      <td>{o.name || '—'}</td>
                      <td>{o.hubId != null ? o.hubId : '—'}</td>
                      <td>
                        {Array.isArray(o.hubs) && o.hubs.length
                          ? o.hubs
                              .slice()
                              .sort((a, b) => (b.isPrimary === true) - (a.isPrimary === true))
                              .map((h) => (h?.hubId != null ? `${h.hubId}${h.isPrimary ? ' (primary)' : ''}` : null))
                              .filter(Boolean)
                              .join(', ')
                          : '—'}
                      </td>
                      <td>{o.description || '—'}</td>
                      <td>{o.contactEmail || '—'}</td>
                      <td>{o.contactPhone || '—'}</td>
                      <td>{o.address || '—'}</td>
                      <td>{o.isActive !== false ? 'Yes' : 'No'}</td>
                      <td>{formatDate(o.createdAt)}</td>
                      <td className="superadmin-actions-cell">
                        <button type="button" className="sa-btn-edit" onClick={() => openOrgEdit(o)} disabled={busy}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="sa-btn-delete"
                          onClick={() => setDeleteTarget({ type: 'org', id: o.id, label: o.name })}
                          disabled={busy || o.isActive === false}
                          title={o.isActive === false ? 'Already inactive' : 'Deactivate organisation'}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {userEdit && (
        <div className="sa-modal-overlay" onClick={() => !busy && setUserEdit(null)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sa-modal-title">Edit User</h3>
            <form onSubmit={saveUserEdit} className="org-create-form">
              <div className="org-field">
                <label>Name</label>
                <input className="org-input" value={userEdit.name} onChange={(e) => setUserEdit({ ...userEdit, name: e.target.value })} required />
              </div>
              <div className="org-field">
                <label>Email</label>
                <input
                  className="org-input"
                  type="email"
                  value={userEdit.email}
                  onChange={(e) => setUserEdit({ ...userEdit, email: e.target.value })}
                  required
                />
              </div>
              <div className="org-field">
                <label>Organisation Name</label>
                <input
                  className="org-input"
                  value={userEdit.organisationName}
                  onChange={(e) => setUserEdit({ ...userEdit, organisationName: e.target.value })}
                  required
                />
              </div>
              <div className="org-field">
                <label>Role</label>
                <select className="org-input" value={userEdit.role} onChange={(e) => setUserEdit({ ...userEdit, role: e.target.value })}>
                  <option value="User">User</option>
                  <option value="SuperAdmin">SuperAdmin</option>
                </select>
              </div>
              <div className="org-field">
                <label className="org-hub-primary">
                  <input
                    type="checkbox"
                    checked={userEdit.isSuperAdmin}
                    onChange={(e) => setUserEdit({ ...userEdit, isSuperAdmin: e.target.checked })}
                  />
                  Super Admin
                </label>
              </div>
              <div className="org-field">
                <label>New Password (optional)</label>
                <input
                  className="org-input"
                  type="password"
                  value={userEdit.password}
                  onChange={(e) => setUserEdit({ ...userEdit, password: e.target.value })}
                  placeholder="Leave blank to keep current password"
                  minLength={8}
                />
              </div>
              <div className="sa-modal-actions">
                <button type="button" className="org-btn-secondary" onClick={() => setUserEdit(null)} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="org-btn-primary" disabled={busy}>
                  {busy ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {orgEdit && (
        <div className="sa-modal-overlay" onClick={() => !busy && setOrgEdit(null)}>
          <div className="sa-modal sa-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3 className="sa-modal-title">Edit Organisation</h3>
            <form onSubmit={saveOrgEdit} className="org-create-form">
              <div className="org-field">
                <label>Name</label>
                <input className="org-input" value={orgEdit.name} onChange={(e) => setOrgEdit({ ...orgEdit, name: e.target.value })} required />
              </div>
              <div className="org-field">
                <label>Organisation Id</label>
                <input
                  className="org-input"
                  type="number"
                  min="0"
                  value={orgEdit.organisationId}
                  onChange={(e) => setOrgEdit({ ...orgEdit, organisationId: e.target.value })}
                />
              </div>
              <div className="org-field">
                <label>Hub IDs</label>
                <div className="org-hub-list">
                  {orgEdit.hubIds.map((v, idx) => (
                    <div key={idx} className="org-hub-row">
                      <label className="org-hub-primary">
                        <input
                          type="radio"
                          name="editPrimaryHub"
                          checked={orgEdit.primaryHubIndex === idx}
                          onChange={() => setOrgEdit({ ...orgEdit, primaryHubIndex: idx })}
                        />
                        Primary
                      </label>
                      <input
                        className="org-input"
                        inputMode="numeric"
                        value={v}
                        onChange={(e) =>
                          setOrgEdit({
                            ...orgEdit,
                            hubIds: orgEdit.hubIds.map((x, i) => (i === idx ? e.target.value : x))
                          })
                        }
                      />
                      <button
                        type="button"
                        className="org-btn-secondary"
                        onClick={() => {
                          if (orgEdit.hubIds.length <= 1) return;
                          const next = orgEdit.hubIds.filter((_, i) => i !== idx);
                          setOrgEdit({
                            ...orgEdit,
                            hubIds: next,
                            primaryHubIndex: idx === orgEdit.primaryHubIndex ? 0 : orgEdit.primaryHubIndex > idx ? orgEdit.primaryHubIndex - 1 : orgEdit.primaryHubIndex
                          });
                        }}
                        disabled={orgEdit.hubIds.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="org-btn-secondary"
                    onClick={() => setOrgEdit({ ...orgEdit, hubIds: [...orgEdit.hubIds, ''] })}
                  >
                    Add another Hub ID
                  </button>
                </div>
              </div>
              <div className="org-field">
                <label>Description</label>
                <input className="org-input" value={orgEdit.description} onChange={(e) => setOrgEdit({ ...orgEdit, description: e.target.value })} />
              </div>
              <div className="org-field">
                <label>Contact Email</label>
                <input className="org-input" type="email" value={orgEdit.contactEmail} onChange={(e) => setOrgEdit({ ...orgEdit, contactEmail: e.target.value })} />
              </div>
              <div className="org-field">
                <label>Contact Phone</label>
                <input className="org-input" value={orgEdit.contactPhone} onChange={(e) => setOrgEdit({ ...orgEdit, contactPhone: e.target.value })} />
              </div>
              <div className="org-field">
                <label>Address</label>
                <input className="org-input" value={orgEdit.address} onChange={(e) => setOrgEdit({ ...orgEdit, address: e.target.value })} />
              </div>
              <div className="org-field">
                <label className="org-hub-primary">
                  <input
                    type="checkbox"
                    checked={orgEdit.isActive}
                    onChange={(e) => setOrgEdit({ ...orgEdit, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="sa-modal-actions">
                <button type="button" className="org-btn-secondary" onClick={() => setOrgEdit(null)} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="org-btn-primary" disabled={busy}>
                  {busy ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="sa-modal-overlay" onClick={() => !busy && setDeleteTarget(null)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sa-modal-title">Confirm Delete</h3>
            <p className="sa-modal-text">
              {deleteTarget.type === 'user'
                ? `Delete user "${deleteTarget.label}"? This cannot be undone.`
                : `Deactivate organisation "${deleteTarget.label}"? It will be marked inactive.`}
            </p>
            <div className="sa-modal-actions">
              <button type="button" className="org-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="sa-btn-delete sa-btn-delete-confirm" onClick={confirmDelete} disabled={busy}>
                {busy ? 'Working...' : deleteTarget.type === 'user' ? 'Delete' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
